import { startCase } from "lodash";
import { ChevronDown, ChevronUp } from "lucide-react";
import { ReactNode, useMemo, useState } from "react";

import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible";
import { Icons } from "./ui/icons";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

import { Segmented } from "@/components/activity/activity-panel";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/providers";
import type { FeatureFlagMetadata } from "@/services/organization.service";
import { isPermitted, Permissions } from "@/utils/permission-manager";

type VisibilityRule = {
  type: string;
  setting_key?: string;
  flag_name?: string;
  action?: string;
  pricing_tier_classes?: string[];
  rule?: VisibilityRule;
  rules?: VisibilityRule[];
};

/**
 * Short, human readable summary of when a feature is shown to customers
 * in the 360 settings Features page. Returns undefined for "always".
 */
const describeVisibilityRule = (rule: VisibilityRule): string | undefined => {
  switch (rule.type) {
    case "always":
      return undefined;
    case "never":
      return "Hidden in 360";
    case "advanced_mode":
      return "Advanced mode";
    case "pricing_tier":
      return `Pricing tier: ${rule.setting_key}`;
    case "feature_flag":
    case "flag_enabled":
      return `Flag: ${rule.flag_name}`;
    case "permission":
      return `Permission: ${rule.action}`;
    case "setting_enabled":
      return `Requires: ${rule.setting_key}`;
    case "pricing_tier_class":
      return `Tier: ${rule.pricing_tier_classes?.join("/")}`;
    case "production_org":
      return "Production org";
    case "not":
      return `Not (${rule.rule ? describeVisibilityRule(rule.rule) : ""})`;
    case "and":
    case "or":
      return rule.rules
        ?.map(describeVisibilityRule)
        .filter(Boolean)
        .join(rule.type === "and" ? " & " : " | ");
    default:
      return rule.type;
  }
};

type OrganizationFeatureSettingsProps = {
  /** Feature toggle metadata, same list as the 360 settings Features page */
  featureFlags: FeatureFlagMetadata[];
  settings: {
    [key: string]: unknown;
  };
  loading?: boolean;
  defaultOpen?: boolean;
  onChange?: (setting: {
    key: string;
    value: { [key: string]: boolean };
  }) => void;
  disabled?: boolean;
  /** rendered at the top of the side column, next to the feature list */
  aside?: ReactNode;
};

type StatusFilter = "all" | "on" | "off";

const isToggleSetting = (value: unknown): value is { enabled: boolean } =>
  typeof (value as { enabled?: unknown })?.enabled === "boolean";

const OrganizationFeatureSettings = ({
  featureFlags,
  settings,
  loading,
  defaultOpen = true,
  onChange,
  disabled = false,
  aside,
}: OrganizationFeatureSettingsProps) => {
  const { userData } = useAuth();
  const [open, setOpen] = useState(defaultOpen);
  const [otherOpen, setOtherOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  const isDisabled =
    !isPermitted(Permissions.EDIT_ORG_SETTINGS, userData) || disabled;

  // toggle settings stored on the org that the 360 Features page doesn't list
  const otherSettings = useMemo(() => {
    const known = new Set(featureFlags.map((flag) => flag.feature_name));

    return Object.entries(settings)
      .filter(([key, value]) => !known.has(key) && isToggleSetting(value))
      .map(([key]) => key)
      .sort();
  }, [featureFlags, settings]);

  const query = searchQuery.trim().toLowerCase();
  const matchesQuery = (...values: (string | undefined)[]) =>
    !query || values.some((v) => v?.toLowerCase().includes(query));

  const isEnabled = (key: string) =>
    (settings[key] as { enabled?: boolean } | undefined)?.enabled ?? false;

  // "on" follows the switch, so inverted toggles count as on when enabled is false
  const matchesStatus = (key: string, reverseChecked?: boolean) =>
    statusFilter === "all" ||
    (reverseChecked ? !isEnabled(key) : isEnabled(key)) ===
      (statusFilter === "on");

  const filteredFeatureFlags = featureFlags.filter(
    (flag) =>
      matchesQuery(
        flag.feature_name,
        flag.title?.default_value,
        flag.description?.default_value
      ) && matchesStatus(flag.feature_name, flag.reverse_checked)
  );
  const filteredOtherSettings = otherSettings.filter(
    (key) => matchesQuery(key, startCase(key)) && matchesStatus(key)
  );

  const renderToggle = ({
    featureName,
    title,
    description,
    badges,
    reverseChecked,
  }: {
    featureName: string;
    title: string;
    description?: string;
    badges?: string[];
    reverseChecked?: boolean;
  }) => {
    const enabled = isEnabled(featureName);

    return (
      <div className="p-2 border border-border rounded-lg" key={featureName}>
        <div className="flex flex-row items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <Label htmlFor={featureName}>{title}</Label>
              <code className="text-xs text-muted-foreground">
                {featureName}
              </code>
              {badges?.map((badge) => (
                <Badge key={badge} variant="secondary">
                  {badge}
                </Badge>
              ))}
            </div>
            {description && (
              <p className="text-xs text-muted-foreground">{description}</p>
            )}
            {reverseChecked && (
              <p className="text-xs text-muted-foreground">
                Inverted toggle: on = <code>enabled: false</code>
              </p>
            )}
          </div>
          <Switch
            loading={loading}
            checked={reverseChecked ? !enabled : enabled}
            onCheckedChange={(checked) => {
              onChange?.({
                key: featureName,
                value: {
                  enabled: reverseChecked ? !checked : checked,
                },
              });
            }}
            disabled={isDisabled}
            id={featureName}
            style={{ marginTop: 0 }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Input
          className="max-w-sm"
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search features..."
          type="text"
          value={searchQuery}
        />
        <Segmented
          ariaLabel="Filter by state"
          onChange={setStatusFilter}
          options={[
            { value: "all", label: "All" },
            { value: "on", label: "On" },
            { value: "off", label: "Off" },
          ]}
          value={statusFilter}
        />
      </div>
      <div className="flex flex-wrap items-start gap-6">
        <Collapsible
          className="min-w-0 flex-1 basis-[36rem] px-4 py-2 space-y-2 border border-border rounded-md"
          onOpenChange={(e) => setOpen(e)}
          open={open}
        >
          <CollapsibleTrigger asChild>
            <div className="flex items-center justify-between cursor-pointer">
              <h4 className="text-sm font-semibold text-primary">
                Features ({filteredFeatureFlags.length})
              </h4>
              <Button
                className="p-0 w-9"
                size="sm"
                type="button"
                variant="ghost"
              >
                {loading ? (
                  <Icons.spinner className="w-4 h-4 mr-0" />
                ) : open ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
                <span className="sr-only">Toggle</span>
              </Button>
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-2">
            <p className="text-xs text-muted-foreground">
              Same list as Settings → Features in the 360 portal. Badges show
              when a feature is visible to customers there.
            </p>
            <div className="max-h-[90vh] overflow-y-auto">
              <div className="space-y-4">
                {filteredFeatureFlags.map((flag) => {
                  const visibility = (
                    (flag.visibility_rules as unknown as VisibilityRule[]) ?? []
                  )
                    .map(describeVisibilityRule)
                    .filter((v): v is string => Boolean(v));

                  return renderToggle({
                    featureName: flag.feature_name,
                    title:
                      flag.title?.default_value ?? startCase(flag.feature_name),
                    description: flag.description?.default_value,
                    badges: [
                      ...(flag.badge ? [flag.badge] : []),
                      ...(flag.one_way ? ["one-way"] : []),
                      ...visibility,
                    ],
                    reverseChecked: flag.reverse_checked,
                  });
                })}
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
        <aside className="w-full min-w-0 space-y-4 lg:w-80 lg:flex-none">
          {aside}
          {otherSettings.length > 0 && (
            <Collapsible
              className="px-4 py-2 space-y-2 border border-border rounded-md"
              onOpenChange={(e) => setOtherOpen(e)}
              open={otherOpen}
            >
              <CollapsibleTrigger asChild>
                <div className="flex items-center justify-between cursor-pointer">
                  <h4 className="text-sm font-semibold text-primary">
                    Other Settings ({filteredOtherSettings.length})
                  </h4>
                  <Button
                    className="p-0 w-9"
                    size="sm"
                    type="button"
                    variant="ghost"
                  >
                    {otherOpen ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                    <span className="sr-only">Toggle</span>
                  </Button>
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Toggle settings stored on this organization that are not
                  listed in the 360 Features page.
                </p>
                <div className="space-y-4">
                  {filteredOtherSettings.map((key) =>
                    renderToggle({ featureName: key, title: startCase(key) })
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}
        </aside>
      </div>
    </div>
  );
};

export default OrganizationFeatureSettings;
